import { ref, watch } from "vue"

/**
 * exported sort direction for Typing
 */
export type SortDirection = "ASC" | "DESC"

/**
 * objectAlphaSort provides a sorting predicate for arrays of objects, allowing you to sort against a desired property
 * regardless of that properties level in the object. Example: Given an array of objects const objArr = [...{label: "label", value: "val"}]
 * use as objArr.sort(objectAlphaSort("ASC", ["label"])) to sort the objects against their label property.
 *
 * @param dir SortDirection the direction to sort in
 * @param props string[] of nested properties, the final properties value will be the sorting value
 * @returns Function sort predicate
 */
export function objectAlphaSort(dir: SortDirection, ...props: string[]) {
  function resolveProp(obj: unknown) {
    return props.reduce((obj, prop) => {
      return obj[prop] !== undefined ? obj[prop] : null
    }, obj)
  }

  return (a, b) => {
    let aVal = resolveProp(a)
    let bVal = resolveProp(b)

    if (typeof aVal === "string") {
      aVal = aVal.toLocaleLowerCase()
    }

    if (typeof bVal === "string") {
      bVal = bVal.toLocaleLowerCase()
    }

    switch (dir) {
      case "ASC":
        if (aVal < bVal) {
          return -1
        }
        if (aVal > bVal) {
          return 1
        }
        return 0
      case "DESC":
        if (aVal > bVal) {
          return -1
        }
        if (aVal < bVal) {
          return 1
        }
        return 0
    }
  }
}

export function useFragment() {
  const fragment = ref<string>(window.location.hash.replace("#", ""))
  const onChange = (fragment: string) => {
    if (fragment === "") {
      window.history.replaceState(
        null,
        document.title,
        `${window.origin}${window.location.pathname}${window.location.search}`
      )
    } else {
      window.location.hash = fragment
      window.history.replaceState(
        { fragment: fragment },
        document.title,
        window.location.href
      )
    }
  }

  watch(fragment, onChange)

  return fragment
}

export const cookie = {
  get(name: string) {
    const cookieName = name + "="
    const cookies = document.cookie.split(";")
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i]
      while (cookie.charAt(0) == " ") {
        cookie = cookie.substring(1)
      }
      if (cookie.indexOf(cookieName) == 0) {
        return cookie.substring(cookieName.length, cookie.length)
      }
    }
    return ""
  },
  set(name: string, value: string | number, duration: number, path = "/") {
    const d = new Date()
    d.setTime(d.getTime() + duration * 24 * 60 * 60 * 1000)
    document.cookie = `${name}=${value};expires=${d.toUTCString()};path=${path}`
  },
}

export const debounce = (fn: (...args: any[]) => void, wait = 500) => {
  let timer = null
  return function (...args) {
    if (timer) {
      clearTimeout(timer)
    }

    timer = setTimeout(() => {
      fn(...args)
    }, wait)
  }
}
